import type { ActivityModalName, ActivitySurface, ActivityVia } from './activity.model'

/**
 * The event catalogue — the single source of truth (names, surfaces, props, the
 * vias an event can carry, its default via and the statechart mapping Phase 2
 * imports). `ActivityEventName` and `ActivityPropsOf<N>` are derived from it, so
 * an unknown event name or prop key is a compile error at every `track()` call.
 * When you add a user action on an Editor screen, add its event here and track it
 * where the action happens.
 *
 * `statechart` maps an event into `.plans/markup-ux/markup-workflow.machine.yaml`
 * step ids: see `resolveStatechartIds`.
 */

/** Statechart ids: no suffix = always applies; `@via` = applies in addition when the event's via equals it. */
export type StatechartIds = readonly string[]
export type StatechartMap =
  | StatechartIds
  | { readonly by: readonly string[]; readonly rows: Readonly<Record<string, StatechartIds>> }

export interface ActivityEventSpec {
  readonly surfaces: readonly ActivitySurface[]
  /** The vias the event can carry; empty = never has one (`via: null`). */
  readonly vias: readonly ActivityVia[]
  /** Used when no trigger resolves to a declared via. */
  readonly defaultVia: ActivityVia | null
  /** Phantom type of the props. */
  readonly props: object
  readonly statechart?: StatechartMap
}

const ALL: readonly ActivitySurface[] = ['task_list', 'segment_editing', 'quick_edit', 'markup', 'group_review']
const EDITORS: readonly ActivitySurface[] = ['segment_editing', 'quick_edit', 'markup']
const EDIT_AND_GROUP: readonly ActivitySurface[] = ['segment_editing', 'quick_edit', 'markup', 'group_review']
/** The editors with a work pane (`list.*`, `item.*`). */
const PANE_EDITORS: readonly ActivitySurface[] = ['markup', 'segment_editing', 'quick_edit']

type Tool = string
/** Why a sketch ended without a feature. `empty`: released before it had any extent (a click on a drag-to-draw tool, a lone Shift-click). */
export type DrawCancelReason = 'escape' | 'tool_switch' | 'space_pan' | 'discard_dialog' | 'empty' | 'other'
/** Why a draw or split click was refused: the Missing Segment tool's rejections plus the split tool's `near_endpoint`. */
export type DrawRejectReason = 'start_not_on_vertex' | 'start_outside_boundary' | 'outside_boundary' | 'crosses_geometry' | 'near_endpoint'
type Origin = 'seeded' | 'drawn'
type SegmentKind = 'segment' | 'shape' | 'missing'
/** Why unapplied attribute edits were dropped (`attr.discarded`). */
export type AttrDiscardReason = 'selection_change' | 'prompt' | 'deleted' | 'undo' | 'closed'
/** What the unapplied-changes prompt interrupted (`attr.prompt`; `complete` is Markup's Complete). */
export type AttrPromptTrigger = 'select' | 'nav' | 'tab' | 'tool' | 'back' | 'merge' | 'submit' | 'complete' | 'leave'

/**
 * The work panes' lists: Markup's four (`'new_segments'` is the Segments tab, the name kept for
 * continuity) and Segment editing's / Quick Edit's Roads and Changes.
 */
export type PaneList = 'inspections' | 'connectors' | 'new_segments' | 'markups' | 'roads' | 'changes'
/** `item.nav`: the pager (‹ › or `,` `.`) or Next to do (button, J, Auto-next). */
export type ItemNavDir = 'prev' | 'next' | 'next_todo'

export const ACTIVITY_EVENTS = {
  // ── Shared core ─────────────────────────────────────────────────────────────
  'session.start': {
    surfaces: ['app'], vias: [], defaultVia: null,
    props: {} as { reason: 'page_load' | 'clock_drift' | 'hidden_gap' | 'owner_mismatch' | 'user_change'; browser: 'chrome' | 'edge' | 'firefox' | 'safari' | 'other'; touch: boolean },
  },
  'route.change': { surfaces: ALL, vias: [], defaultVia: null, props: {} as { from: string; to: string } },
  'visibility': { surfaces: ALL, vias: [], defaultVia: null, props: {} as { state: 'hidden' | 'visible'; hiddenMs?: number } },
  'focus': { surfaces: ALL, vias: [], defaultVia: null, props: {} as { state: 'focus' | 'blur' } },
  'modal.open': { surfaces: ALL, vias: [], defaultVia: null, props: {} as { name: ActivityModalName } },
  'modal.close': { surfaces: ALL, vias: [], defaultVia: null, props: {} as { name: ActivityModalName; ms: number } },
  'shortcut.ignored': {
    surfaces: EDIT_AND_GROUP, vias: ['key'], defaultVia: null,
    props: {} as { reason: 'form_field' | 'modal' | 'menu_open' | 'repeat' | 'not_applicable'; field?: 'input' | 'textarea' | 'select' },
    statechart: { by: ['reason'], rows: { form_field: ['H-12'] } },
  },
  'wait.slow': { surfaces: ALL, vias: ['auto'], defaultVia: 'auto', props: {} as { kind: string; ms: number; status: number } },
  'request.error': { surfaces: ALL, vias: ['auto'], defaultVia: 'auto', props: {} as { kind: string; status: number } },
  'ws.close': { surfaces: ALL, vias: ['auto'], defaultVia: 'auto', props: {} as { code: number; clean: boolean; lifetimeMs: number } },
  'tutorial.open': {
    surfaces: EDIT_AND_GROUP, vias: ['click', 'menu', 'key', 'auto'], defaultVia: 'click',
    props: {} as { auto: boolean },
    statechart: { by: ['auto'], rows: { true: ['O-01', 'X-04'], false: ['O-01'] } },
  },
  'shortcuts.open': { surfaces: EDITORS, vias: ['click', 'menu', 'key'], defaultVia: 'click', props: {} as object, statechart: ['O-02'] },
  'shortcuts.close': { surfaces: EDITORS, vias: ['click', 'menu', 'key'], defaultVia: 'click', props: {} as object },
  'palette.open': { surfaces: EDITORS, vias: ['click', 'menu'], defaultVia: 'menu', props: {} as object, statechart: ['O-04'] },
  'layer.change': {
    surfaces: ALL, vias: ['click'], defaultVia: 'click',
    props: {} as { layer: string; change: 'visible' | 'opacity' | 'basemap' | 'order' | 'label' },
  },

  // ── Map editing ─────────────────────────────────────────────────────────────
  // `auto`: a programmatic start (a Suggested connector's Yes places the first vertex, `startMissingSegmentAt`).
  'draw.start': { surfaces: EDITORS, vias: ['map', 'auto'], defaultVia: 'map', props: {} as { tool: Tool } },
  'draw.end': {
    surfaces: EDITORS, vias: ['key', 'map'], defaultVia: 'map',
    props: {} as { tool: Tool; ms: number; vertices?: number; finish: 'enter' | 'click' | 'release' | 'snap'; startSnap?: string; endSnap?: string },
    statechart: {
      by: ['tool', 'finish'],
      rows: {
        'draw-rectangle+*': ['D-01'], 'draw-oval+*': ['D-02'], 'draw-point+*': ['D-03'],
        'draw-polygon+enter': ['D-04', 'D-04c'], 'draw-polygon+*': ['D-04'],
        'draw-line+*': ['D-05'], 'draw-missing-segment+*': ['D-06'],
      },
    },
  },
  'draw.cancel': {
    surfaces: EDITORS, vias: ['key', 'auto'], defaultVia: 'auto',
    props: {} as { tool: Tool; reason: DrawCancelReason; ms: number; vertices?: number },
    statechart: { by: ['reason'], rows: { escape: ['D-08'], space_pan: ['D-12'], tool_switch: ['D-12'] } },
  },
  'draw.rejected': {
    surfaces: EDITORS, vias: ['map'], defaultVia: 'map', props: {} as { tool: Tool; reason: DrawRejectReason }, statechart: ['D-07'],
  },
  'draw.vertexRemoved': { surfaces: EDITORS, vias: ['key'], defaultVia: 'key', props: {} as { tool: Tool }, statechart: ['D-09'] },
  'map.click': {
    surfaces: EDITORS, vias: ['map'], defaultVia: 'map',
    props: {} as { hit: 'vertex' | 'segment' | 'reference' | 'shape' | 'inspection' | 'missing' | 'boundary' | 'empty'; tool: Tool },
  },
  'tool.change': {
    surfaces: EDITORS, vias: ['click', 'menu', 'key', 'auto'], defaultVia: 'click',
    props: {} as { from: Tool; to: Tool },
    statechart: {
      by: ['to'],
      rows: {
        pan: ['T-01@click', 'T-02@key'], modify: ['T-01@click', 'T-02@key'],
        select: ['T-01@click', 'T-02@key', 'H-01@auto'],
        'draw-rectangle': ['T-03@menu', 'T-04@key'], 'draw-oval': ['T-03@menu', 'T-04@key'],
        'draw-polygon': ['T-03@menu', 'T-04@key'], 'draw-line': ['T-03@menu', 'T-04@key'],
        'draw-point': ['T-03@menu', 'T-04@key'], 'draw-missing-segment': ['T-05@menu', 'T-06@key'],
      },
    },
  },
  'modify.drag': {
    surfaces: EDITORS, vias: ['map'], defaultVia: 'map', props: {} as { kind: 'segment' | 'shape'; ms: number; origin?: Origin },
    statechart: ['A-05'],
  },
  'vertex.delete': {
    surfaces: EDITORS, vias: ['key', 'map'], defaultVia: 'key', props: {} as { kind: 'segment' | 'shape'; ok: boolean; origin?: Origin },
  },
  'select': {
    surfaces: EDITORS, vias: ['map', 'list', 'auto', 'key'], defaultVia: 'map',
    props: {} as { kind: 'segment' | 'shape' | 'inspection' | 'missing' | 'connector' | 'none'; count: number },
    statechart: {
      by: ['kind'],
      rows: { shape: ['S-01', 'A-04'], inspection: ['S-02', 'I-01@map', 'I-02@list'], missing: ['S-03'], none: ['S-04'] },
    },
  },
  'delete': {
    surfaces: EDITORS, vias: ['click', 'key'], defaultVia: 'click', props: {} as { kind: SegmentKind; count: number; origin?: Origin },
    statechart: { by: ['kind'], rows: { shape: ['A-06'], missing: ['M-04'] } },
  },
  'undo': { surfaces: EDITORS, vias: ['click', 'key'], defaultVia: 'click', props: {} as { type?: string }, statechart: ['A-07'] },
  'redo': { surfaces: EDITORS, vias: ['click', 'key'], defaultVia: 'click', props: {} as { type?: string }, statechart: ['A-07'] },
  'peek': { surfaces: ['task_list', ...EDITORS], vias: ['key', 'click'], defaultVia: 'click', props: {} as { ms: number }, statechart: ['T-08'] },
  'peek.flag': {
    surfaces: EDITORS, vias: ['menu'], defaultVia: 'menu',
    props: {} as { flag: 'keepAdditionalLayers' | 'keepDataLayers'; on: boolean },
  },
  'spacepan': {
    surfaces: ALL, vias: ['key'], defaultVia: 'key', props: {} as { ms: number; moved: boolean; sketchLost: boolean },
    statechart: { by: ['sketchLost'], rows: { true: ['T-07', 'D-12'], '*': ['T-07'] } },
  },
  'task.open': { surfaces: EDITORS, vias: [], defaultVia: null, props: {} as { loadMs: number; reviewerNoteLen: number }, statechart: ['X-03'] },
  'task.ready': { surfaces: EDITORS, vias: [], defaultVia: null, props: {} as { loadMs: number } },
  'task.leave': { surfaces: EDITORS, vias: [], defaultVia: null, props: {} as { ms: number; reason: 'completed' | 'navigated' } },
  'complete.click': { surfaces: EDITORS, vias: ['click'], defaultVia: 'click', props: {} as { action: 'submit' | 'complete' }, statechart: ['C-01'] },
  /** Markup's Complete guard; Segment editing's Submit guard (`needs_attributes`: new roads without attributes). */
  'complete.guard': {
    surfaces: ['markup', 'segment_editing'], vias: ['auto'], defaultVia: 'auto',
    props: {} as { kind: 'unconfirmed_missing' | 'unanswered_inspections' | 'needs_attributes'; count: number },
    statechart: { by: ['kind'], rows: { unanswered_inspections: ['C-02'], unconfirmed_missing: ['C-03'] } },
  },
  'complete.confirm': { surfaces: EDITORS, vias: ['click'], defaultVia: 'click', props: {} as { dialog: 'finalize' | 'unanswered' }, statechart: ['C-01c'] },
  'complete.cancel': { surfaces: EDITORS, vias: ['click', 'key'], defaultVia: 'click', props: {} as { dialog: 'finalize' | 'unanswered' } },
  'complete.done': {
    surfaces: EDITORS, vias: ['auto'], defaultVia: 'auto', props: {} as { action: 'submit' | 'complete'; drainMs: number; serverMs: number },
    statechart: ['C-01d', 'C-01e', 'X-05'],
  },
  'complete.failed': {
    surfaces: EDITORS, vias: ['auto'], defaultVia: 'auto',
    props: {} as { action: 'submit' | 'complete'; stage: 'drain' | 'save_error' | 'server'; status?: number }, statechart: ['C-01f'],
  },

  // ── Segment editing / Quick Edit ────────────────────────────────────────────
  'segment.split': { surfaces: ['segment_editing', 'quick_edit'], vias: ['map'], defaultVia: 'map', props: {} as { ok: boolean; origin?: Origin } },
  'segment.merge': {
    surfaces: ['segment_editing', 'quick_edit'], vias: ['click'], defaultVia: 'click',
    props: {} as { ok: boolean; error?: 'no_shared_endpoint' | 'attribute_conflict'; origin?: Origin },
  },
  'snap.toggle': { surfaces: ['segment_editing', 'quick_edit'], vias: ['click', 'key'], defaultVia: 'click', props: {} as { on: boolean } },
  'attr.save': {
    // `key`: Ctrl/⌘+Enter on the Markup Segments detail and on Segment editing's road / multi detail.
    surfaces: EDITORS, vias: ['click', 'key'], defaultVia: 'click', props: {} as { fieldsChanged: string[]; count: number; origin?: Origin },
  },
  'attr.cancel': { surfaces: EDITORS, vias: ['click'], defaultVia: 'click', props: {} as { ms: number } },
  /**
   * Unapplied attribute edits dropped: `selection_change` (the form kept for another item without
   * asking — since Markup's prompt only a backstop, kept for the stored history), `prompt` (Discard
   * in the unapplied-changes prompt), `deleted` (the edited road or segment deleted), `undo` (Undo
   * or Redo), `closed` (the form destroyed any other way).
   */
  'attr.discarded': {
    surfaces: EDITORS, vias: ['auto'], defaultVia: 'auto',
    props: {} as { ms: number; reason: AttrDiscardReason },
  },
  /**
   * Segment editing's and Markup's unapplied-changes prompt answered: what the Editor was doing
   * when asked (`trigger`), the answer, and how long the prompt was open.
   */
  'attr.prompt': {
    surfaces: ['segment_editing', 'markup'], vias: ['click', 'key'], defaultVia: 'click',
    props: {} as { trigger: AttrPromptTrigger; answer: 'apply' | 'discard' | 'keep'; ms: number },
  },
  /** Start road / End road: a drawn road's attributes copied from the road its end touches. */
  'attr.copy': { surfaces: ['segment_editing'], vias: ['click'], defaultVia: 'click', props: {} as { from: 'start' | 'end' } },
  /** A deleted road restored from its Changes detail (`parts`: the deletions removed). */
  'segment.restore': {
    surfaces: ['segment_editing', 'quick_edit'], vias: ['click'], defaultVia: 'click', props: {} as { parts: number; origin?: Origin },
  },

  // ── Work panes (Markup; Segment editing and Quick Edit) ──────────────────────
  /**
   * A work pane tab was activated (its strip, its key — I/C/S/A, R/E —, the guard's Show, the crumb).
   * `from`: what the active tab showed before — `'detail'` with an item open, else `'list'`; a same-tab
   * activation from a detail is "back to the list".
   */
  'list.open': {
    surfaces: PANE_EDITORS, vias: ['click', 'key'], defaultVia: 'click',
    props: {} as { list: PaneList; badge: number; from: 'list' | 'detail' },
    statechart: { by: ['list'], rows: { inspections: ['L-01'], markups: ['L-02'], new_segments: ['L-03'], connectors: ['L-04'] } },
  },
  /** A list row picked (click, or Enter on the row). */
  'list.pick': {
    surfaces: PANE_EDITORS, vias: ['list'], defaultVia: 'list', props: {} as { list: PaneList; index: number; of: number },
  },
  /** The pager or Next to do in any tab; `found: false` when there was nowhere to go. */
  'item.nav': {
    surfaces: PANE_EDITORS, vias: ['click', 'key', 'auto'], defaultVia: 'click',
    props: {} as { list: PaneList; dir: ItemNavDir; found: boolean },
    statechart: { by: ['dir'], rows: { next_todo: ['I-05@click', 'I-05@key', 'I-05b@auto'], prev: ['N-01'], next: ['N-01'] } },
  },
  // ── Markup ──────────────────────────────────────────────────────────────────
  /** The footer's persisted Auto-next switch (Inspections, Connectors). */
  'autonext.toggle': { surfaces: ['markup'], vias: ['click'], defaultVia: 'click', props: {} as { on: boolean }, statechart: ['I-06'] },
  /** The open item's zoom (the header's ⌖ or Z), every tab. */
  'item.zoom': { surfaces: PANE_EDITORS, vias: ['click', 'key'], defaultVia: 'click', props: {} as { list: PaneList }, statechart: ['I-07'] },
  /** A markup note committed (1.5 s idle, blur, or before anything acts on its draft). Lengths only, never the text. */
  'annotation.note': {
    surfaces: ['markup'], vias: ['auto'], defaultVia: 'auto', props: {} as { chars: number; msEditing: number }, statechart: ['A-01'],
  },
  'flag.toggle': { surfaces: ['markup'], vias: ['click', 'key'], defaultVia: 'click', props: {} as { on: boolean }, statechart: ['A-03'] },
  'inspection.answer': {
    surfaces: ['markup'], vias: ['click', 'key'], defaultVia: 'click',
    props: {} as { state: 'confirmed' | 'not_present' | 'new'; autoAdvance: boolean; msSinceSelect: number }, statechart: ['I-03'],
  },
  'inspection.notes': { surfaces: ['markup'], vias: ['auto'], defaultVia: 'auto', props: {} as { chars: number }, statechart: ['I-04'] },

  // ── Retired (see RETIRED_EVENTS): no statechart mapping, kept for the history in "Where time goes" ──
  'annotation.edit.open': { surfaces: ['markup'], vias: ['click'], defaultVia: 'click', props: {} as { hadText: boolean } },
  'annotation.save': {
    surfaces: ['markup'], vias: ['click'], defaultVia: 'click', props: {} as { chars: number; msEditing: number; changed: boolean },
  },
  'annotation.cancel': { surfaces: ['markup'], vias: ['click'], defaultVia: 'click', props: {} as { msEditing: number } },
  'annotation.discarded': {
    surfaces: ['markup'], vias: ['auto'], defaultVia: 'auto',
    props: {} as { reason: 'selection_change' | 'panel_closed'; chars: number; msEditing: number },
  },
  'inspection.next': { surfaces: ['markup'], vias: ['click', 'auto'], defaultVia: 'click', props: {} as { found: boolean } },
  'inspection.auto.toggle': { surfaces: ['markup'], vias: ['click'], defaultVia: 'click', props: {} as { on: boolean } },
  'inspection.zoom': { surfaces: ['markup'], vias: ['click'], defaultVia: 'click', props: {} as object },
  'connectors.toggle': { surfaces: ['markup'], vias: ['click', 'key'], defaultVia: 'click', props: {} as { open: boolean; badge: number } },
  'connector.pick': { surfaces: ['markup'], vias: ['list', 'auto'], defaultVia: 'list', props: {} as { index: number; of: number } },
  // The task list's header button, gone with the Editor home page (2026-10): the home is untracked.
  'group.open': { surfaces: ['task_list'], vias: ['click'], defaultVia: 'click', props: {} as object },
  // The attribute sidebar's pencil, gone with the Segment editing work pane (2026-11): attributes are always editable.
  'attr.edit.open': { surfaces: EDITORS, vias: ['click'], defaultVia: 'click', props: {} as { count: number } },

  'missing.copy': {
    surfaces: ['markup'], vias: ['click'], defaultVia: 'click', props: {} as { from: 'start' | 'end' },
    statechart: { by: ['from'], rows: { start: ['M-01b'], end: ['M-01c'] } },
  },
  'missing.apply': {
    // `key`: Ctrl/⌘+Enter, also from inside the form's fields.
    surfaces: ['markup'], vias: ['click', 'key'], defaultVia: 'click', props: {} as { fieldsChanged: string[]; copiedFrom: 'start' | 'end' | null },
    statechart: { by: ['copiedFrom'], rows: { start: ['M-01d', 'M-01'], end: ['M-01d', 'M-01'], '-': ['M-01d', 'M-02'] } },
  },

  // ── Markup: Suggested connectors (no statechart step) ───────────────────────
  /** The task's suggestions arrived (or not): `ms` is the ensure request, `computedNow` whether it computed them. */
  'connectors.load': {
    surfaces: ['markup'], vias: ['auto'], defaultVia: 'auto',
    props: {} as { count: number; unanswered: number; computedNow: boolean; ms: number; outcome: 'ok' | 'skipped' | 'unavailable' },
  },
  // The tab, its rows and its pager are `list.open` / `list.pick` / `item.nav` with `list: 'connectors'`.
  /** A Yes press starts the draw (`started: false` when refused); the recorded Yes is `connector.linked`. */
  'connector.answer': {
    surfaces: ['markup'], vias: ['click', 'key'], defaultVia: 'click',
    props: {} as { answer: 'yes' | 'no' | 'reset' | 'link'; started?: boolean; msSinceSelect: number },
  },
  /** `endOffsetM`, `endSnap` and `vertices` measure the vertex-only end-snap risk (plan §2.4). */
  'connector.linked': {
    surfaces: ['markup'], vias: ['auto'], defaultVia: 'auto',
    props: {} as {
      how: 'draw' | 'existing'; msSinceYes?: number; endOffsetM?: number
      endSnap?: 'road' | 'missing' | 'boundary' | 'none'; vertices?: number
    },
  },
  'connector.unlinked': { surfaces: ['markup'], vias: ['auto'], defaultVia: 'auto', props: {} as { reason: 'segment_deleted' } },

  // ── Editor task list ────────────────────────────────────────────────────────
  'list.view': { surfaces: ['task_list'], vias: [], defaultVia: null, props: {} as { available: number; needsFixes: number; inProgress: number; projects: number } },
  'list.reload': {
    surfaces: ['task_list'], vias: ['auto'], defaultVia: 'auto',
    props: {} as { cause: 'init' | 'filter' | 'ws_message' | 'ws_reconnect'; ms: number; count: number; ok: boolean },
  },
  'list.filter': { surfaces: ['task_list'], vias: ['click'], defaultVia: 'click', props: {} as { selected: number; total: number } },
  'list.select': { surfaces: ['task_list'], vias: ['map', 'list'], defaultVia: null, props: {} as { taskId: string } },
  'task.start': {
    surfaces: ['task_list'], vias: ['click'], defaultVia: 'click',
    props: {} as { taskId: string; kind: 'start' | 'resume'; ok: boolean; ms: number; status?: number }, statechart: ['X-01'],
  },
  'task.continue': { surfaces: ['task_list'], vias: ['click'], defaultVia: 'click', props: {} as { taskId: string }, statechart: ['X-02'] },

  // ── Inspection Group review ─────────────────────────────────────────────────
  'group.claim': {
    surfaces: ['group_review'], vias: ['auto'], defaultVia: 'auto',
    props: {} as { ms: number; result: 'group' | 'empty'; lost: number; retries: number; prefetch: boolean },
  },
  'group.shown': { surfaces: ['group_review'], vias: ['auto'], defaultVia: 'auto', props: {} as { groupId: string; prefetchHit: boolean; waitMs: number } },
  'group.images.loaded': {
    surfaces: ['group_review'], vias: ['auto'], defaultVia: 'auto',
    props: {} as { groupId: string; perVariantMs: { naip: number; naip_overlaid: number; slope: number; slope_overlaid: number }; failed: number },
  },
  'group.answer': {
    surfaces: ['group_review'], vias: ['click', 'key'], defaultVia: 'click',
    props: {} as { groupId: string; answer: 'yes' | 'no' | 'cant_tell'; needsEdit: boolean; msFromImagesLoaded: number; msShown: number },
  },
  'group.refused': {
    surfaces: ['group_review'], vias: ['click', 'key'], defaultVia: 'click',
    props: {} as { action: 'answer' | 'skip' | 'undo'; reason: 'guard' | 'saving' | 'images' | 'undoing' | 'paused' },
  },
  'group.skip': { surfaces: ['group_review'], vias: ['click', 'key'], defaultVia: 'click', props: {} as { groupId: string; msShown: number } },
  'group.undo': { surfaces: ['group_review'], vias: ['click', 'key'], defaultVia: 'click', props: {} as { outcome: 'started' | 'queued' | 'nothing' } },
  'group.undo.result': { surfaces: ['group_review'], vias: ['auto'], defaultVia: 'auto', props: {} as { ok: boolean; code?: string; ms: number } },
  'group.needsEdit.toggle': { surfaces: ['group_review'], vias: ['click', 'key'], defaultVia: 'click', props: {} as { on: boolean } },
  'group.toggle': { surfaces: ['group_review'], vias: ['click', 'key'], defaultVia: 'click', props: {} as { which: 'base' | 'overlay' } },
  'group.fullscreen': { surfaces: ['group_review'], vias: ['click', 'key'], defaultVia: 'click', props: {} as { on: boolean } },
  'group.more': { surfaces: ['group_review'], vias: ['click', 'key'], defaultVia: 'click', props: {} as { open: boolean } },
  'group.pause': { surfaces: ['group_review'], vias: ['auto'], defaultVia: 'auto', props: {} as { reason: 'idle' } },
  'group.resume': { surfaces: ['group_review'], vias: ['click'], defaultVia: 'click', props: {} as object },
  'group.empty': { surfaces: ['group_review'], vias: ['auto'], defaultVia: 'auto', props: {} as { kind: 'all_answered' | 'none' | 'busy' } },
  'group.checkAgain': { surfaces: ['group_review'], vias: ['click', 'auto'], defaultVia: 'click', props: {} as object },
  'group.images.retry': { surfaces: ['group_review'], vias: ['click'], defaultVia: 'click', props: {} as { count: number } },
  /** A project row picked on the Inspections list; `msOnList` from the list rendering to the pick. */
  'group.project.pick': { surfaces: ['group_review'], vias: ['click'], defaultVia: 'click', props: {} as { project: string; version: number; msOnList: number } },
} as const satisfies Record<string, ActivityEventSpec>

export type ActivityEventName = keyof typeof ACTIVITY_EVENTS
export type ActivityPropsOf<N extends ActivityEventName> = (typeof ACTIVITY_EVENTS)[N]['props']

/**
 * Events no longer emitted (the Markup work pane, 2026-10). They stay in the catalogue, with their
 * labels, detail labels and rollup entries, so rows already stored keep reading in "Where time goes";
 * they have no statechart mapping, so old rows are never costed against redefined steps. `track()`
 * refuses them at compile time ({@link TrackableEventName}). Delete them, together with their
 * labels and `ACTION_ROLLUP_SPECS` / `FRICTION_SPECS` entries, after 2026-12-31 (60-day retention
 * plus margin).
 */
export const RETIRED_EVENTS = [
  'inspection.next', 'inspection.auto.toggle', 'inspection.zoom',
  'annotation.edit.open', 'annotation.save', 'annotation.cancel', 'annotation.discarded',
  'connectors.toggle', 'connector.pick',
  // group.open: remove after 2027-01-31 (retired with the Editor home page, 2026-10; its own date).
  'group.open',
  // attr.edit.open: remove after 2027-02-28 (retired with the Segment editing work pane, 2026-11; its own
  // date, at least 60 days after that release reaches prod — move it if the release ships after 2026-12-29).
  'attr.edit.open',
] as const satisfies readonly ActivityEventName[]
export type RetiredEventName = typeof RETIRED_EVENTS[number]
/** Every event `track()` accepts: the catalogue minus {@link RETIRED_EVENTS}. */
export type TrackableEventName = Exclude<ActivityEventName, RetiredEventName>

/** Re-export for specs and Phase 2. */
export type { ActivityVia }

// ── Statechart resolution ─────────────────────────────────────────────────────

const rowKey = (by: readonly string[], via: ActivityVia | null, props: Readonly<Record<string, unknown>>): string =>
  by.map(name => {
    const v = name === 'via' ? via : props[name]
    return v === null || v === undefined ? '-' : String(v)
  }).join('+')

/** Wildcard rows matching `key` (`*` in any part), fewest `*` first. */
function bestRow(rows: Readonly<Record<string, StatechartIds>>, key: string): StatechartIds | undefined {
  if (key in rows) return rows[key]
  const parts = key.split('+')
  let best: { ids: StatechartIds; stars: number } | undefined
  for (const [rk, ids] of Object.entries(rows)) {
    const rp = rk.split('+')
    if (rp.length !== parts.length || !rp.every((p, i) => p === '*' || p === parts[i])) continue
    const stars = rp.filter(p => p === '*').length
    if (!best || stars < best.stars) best = { ids, stars }
  }
  return best?.ids
}

/**
 * The statechart step ids an event maps to. An id without a suffix always applies;
 * an `@via` id applies in addition when the event's via equals the suffix. In the
 * `{by, rows}` form the row is chosen by the values of the `by` names (`via` reads
 * the event's via, anything else `props[name]`, null/absent = `-`): an exact row
 * wins, else the wildcard row with the fewest `*`, else no ids.
 */
export function resolveStatechartIds(
  map: StatechartMap | undefined, event: { via: ActivityVia | null; props?: Readonly<Record<string, unknown>> },
): string[] {
  if (!map) return []
  const list = Array.isArray(map)
    ? (map as StatechartIds)
    : bestRow((map as { by: readonly string[]; rows: Readonly<Record<string, StatechartIds>> }).rows,
        rowKey((map as { by: readonly string[] }).by, event.via, event.props ?? {})) ?? []
  const out: string[] = []
  for (const id of list) {
    const at = id.indexOf('@')
    if (at < 0) out.push(id)
    else if (event.via === id.slice(at + 1)) out.push(id.slice(0, at))
  }
  return out
}
